import { Link } from '@tanstack/react-router';
import { ArrowLeft, MoreHorizontal, type LucideIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/authStore';
import { hasPermission, type Permission } from '@/lib/rbac';
import { cn } from '@/lib/utils';

export type PageHeaderActionVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';

export interface PageHeaderAction {
  label: string;
  onClick?: () => void;
  to?: string;
  icon?: LucideIcon;
  variant?: PageHeaderActionVariant;
  requiredPermission?: Permission;
  disabled?: boolean;
}

export interface PageHeaderProps {
  title: string;
  description?: string;
  backTo?: { to: string; label: string };
  actions?: PageHeaderAction[];
  variant?: 'default' | 'hero';
}

const BUTTON_VARIANT_MAP: Record<
  PageHeaderActionVariant,
  'primary' | 'outline' | 'ghost' | 'outline-destructive'
> = {
  primary: 'primary',
  secondary: 'outline',
  ghost: 'ghost',
  // Page-level destructive CTAs (e.g. "Delete location") use the outlined
  // red treatment — clearly destructive but not visually screaming. Solid
  // red is reserved for the ConfirmDialog's confirm button where the user
  // is one click away from the destructive action.
  destructive: 'outline-destructive',
};

function ActionButton({ action }: { action: PageHeaderAction }) {
  const Icon = action.icon;
  const variant = BUTTON_VARIANT_MAP[action.variant ?? 'secondary'];

  if (action.to) {
    return (
      <Button asChild variant={variant} size="md" disabled={action.disabled}>
        <Link to={action.to as never}>
          {Icon ? <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" /> : null}
          {action.label}
        </Link>
      </Button>
    );
  }

  return (
    <Button variant={variant} size="md" onClick={action.onClick} disabled={action.disabled}>
      {Icon ? <Icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" /> : null}
      {action.label}
    </Button>
  );
}

function OverflowMenu({ actions }: { actions: PageHeaderAction[] }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="More actions">
          <MoreHorizontal className="h-4 w-4" strokeWidth={1.5} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={4}>
        {actions.map((action) => (
          <DropdownMenuItem
            key={action.label}
            onClick={action.onClick}
            className={cn(
              action.variant === 'destructive' &&
                'text-error-700 focus:text-error-700 focus:bg-error-500/10',
            )}
            disabled={action.disabled}
          >
            {action.icon ? (
              <action.icon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
            ) : null}
            {action.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function PageHeader({
  title,
  description,
  backTo,
  actions,
  variant = 'default',
}: PageHeaderProps) {
  const user = useAuthStore((s) => s.user);
  const role = user?.role ?? null;

  const visibleActions = (actions ?? []).filter(
    (a) => !a.requiredPermission || hasPermission(role, a.requiredPermission),
  );

  const primary = visibleActions.find((a) => a.variant === 'primary');
  const secondaries = visibleActions.filter((a) => a.variant !== 'primary');
  // Show up to 2 secondaries inline — collapsing a single secondary into a
  // kebab menu reads as hidden state. Genuine overflow only kicks in at 3+
  // secondary actions, where the inline row would otherwise feel crowded.
  const inlineSecondaries = secondaries.slice(0, 2);
  const overflow = secondaries.slice(2);

  return (
    <header
      className={cn(
        'flex flex-col gap-2',
        variant === 'default' && 'border-b border-(--color-border) pb-4',
      )}
    >
      {backTo ? (
        <Link
          to={backTo.to as never}
          className={cn(
            'text-meta-sm inline-flex items-center gap-1 self-start text-neutral-500',
            'transition-colors duration-150 hover:text-neutral-900',
            'focus-visible:border-primary-300 rounded-sm focus-visible:shadow-(--shadow-focus-ring) focus-visible:outline-none',
          )}
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          {backTo.label}
        </Link>
      ) : null}

      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h1
            className={cn('font-display text-display-sm text-accent-ink font-medium', 'truncate')}
            title={title}
          >
            {title}
          </h1>
          {description ? (
            <p className="text-body-sm mt-1 line-clamp-2 text-neutral-500">{description}</p>
          ) : null}
        </div>

        {visibleActions.length > 0 ? (
          <div className="flex items-center gap-2">
            {inlineSecondaries.map((action) => (
              <ActionButton key={action.label} action={action} />
            ))}
            {overflow.length > 0 ? <OverflowMenu actions={overflow} /> : null}
            {primary ? <ActionButton action={primary} /> : null}
          </div>
        ) : null}
      </div>
    </header>
  );
}
